import {
  ATTRIBUTION_VERBS,
  COORDINATORS,
  DETERMINERS,
  isAuxiliary,
  MID_ADVERBS,
  NOUN_SIGNALS,
  PREPOSITIONS,
  SUBJECT_PRONOUNS,
  SUBORDINATORS,
  VERB_BASES,
  VERB_FORMS,
} from "@/lib/tool-kit/style-checkers/a-lexicon";
import {
  bracketDepth,
  capitalizeFirst,
  excerpt,
  startsUpper,
  type Word,
  wordsIn,
} from "@/lib/tool-kit/style-checkers/a-words";
import {
  countWords,
  headingFlags,
  insideAny,
  quotedSpans,
  type Sentence,
  type Span,
  splitSentences,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * A comma splice joins two independent clauses with only a comma: "The data were clean,
 * they were ready to use." This module finds the likely ones with rules, not a parser.
 *
 * It looks at every comma that is followed by a space. A comma is reported when the
 * words after it start a clause on their own (a subject pronoun and a verb, "there is",
 * "this means", a noun phrase followed by an auxiliary, or a conjunctive adverb such as
 * "however" that is followed by one of those) and the words before it form a clause that
 * can stand alone. A comma is left alone when a coordinating conjunction (for, and, nor,
 * but, or, yet, so) follows it, when the left side opens with a subordinator, a
 * preposition or an introductory word, when it sits inside quotation marks or brackets,
 * and when the right side is a "he said" style tag.
 *
 * Known limits, stated on the page: no part-of-speech tagging, so clauses that begin
 * with an unlisted verb or a noun the lists do not know are missed (false negatives);
 * and deliberate series of short parallel clauses are reported but marked "possible".
 */

export type SpliceKind =
  | "pronoun"
  | "there"
  | "demonstrative"
  | "determiner"
  | "name"
  | "adverb";

export type SpliceConfidence = "likely" | "possible";

export type SpliceNote = "short" | "parallel" | "series";

export type SpliceFixKind = "period" | "semicolon" | "conjunction";

export interface SpliceFix {
  kind: SpliceFixKind;
  /** The whole sentence with this one comma repaired. */
  sentence: string;
}

export interface SpliceFinding {
  /** Highlight: the last word before the comma through the first word after it. */
  start: number;
  end: number;
  /** Offset of the comma itself. */
  comma: number;
  kind: SpliceKind;
  confidence: SpliceConfidence;
  note: SpliceNote | null;
  /** The conjunctive adverb after the comma ("however"), if there is one. */
  adverb: string | null;
  sentenceStart: number;
  sentence: string;
  leftClause: string;
  rightClause: string;
  fixes: SpliceFix[];
}

export interface CommaSpliceReport {
  words: number;
  sentences: number;
  /** Commas followed by a space that were examined (outside quotes and brackets). */
  commas: number;
  findings: SpliceFinding[];
  likely: number;
  possible: number;
  /** Sentences containing at least one finding. */
  sentencesWithSplice: number;
}

/* ------------------------------------------------------------------ word lists */

/** Single-word conjunctive adverbs and transitions that often follow a splicing comma. */
const CONJ_ADVERBS: ReadonlySet<string> = new Set([
  "however",
  "therefore",
  "thus",
  "moreover",
  "furthermore",
  "consequently",
  "nevertheless",
  "nonetheless",
  "otherwise",
  "instead",
  "hence",
  "accordingly",
  "besides",
  "likewise",
  "similarly",
  "meanwhile",
  "subsequently",
  "additionally",
  "indeed",
  "still",
  "then",
  "afterward",
  "afterwards",
  "conversely",
  "incidentally",
  "regardless",
  "anyway",
  "specifically",
  "rather",
]);

/** Multi-word transitions, checked before the single words. */
const TRANSITION_PHRASES: readonly string[][] = [
  ["on", "the", "other", "hand"],
  ["as", "a", "result"],
  ["in", "addition"],
  ["in", "contrast"],
  ["by", "contrast"],
  ["in", "fact"],
  ["for", "example"],
  ["for", "instance"],
  ["in", "other", "words"],
  ["in", "conclusion"],
  ["at", "the", "same", "time"],
  ["of", "course"],
];

/** Conjunctive adverb → the coordinating conjunction that does the same job. */
const ADVERB_TO_CONJUNCTION: ReadonlyMap<string, string> = new Map([
  ["however", "but"],
  ["nevertheless", "yet"],
  ["nonetheless", "yet"],
  ["therefore", "so"],
  ["thus", "so"],
  ["hence", "so"],
  ["consequently", "so"],
  ["accordingly", "so"],
  ["moreover", "and"],
  ["furthermore", "and"],
  ["additionally", "and"],
  ["otherwise", "or"],
  ["then", "and then"],
]);

/** After these, the comma is the correct one: an introductory word or phrase. */
const INTRO_WORDS: ReadonlySet<string> = new Set([
  "yes",
  "no",
  "well",
  "oh",
  "okay",
  "first",
  "second",
  "third",
  "finally",
  "next",
  "today",
  "yesterday",
  "tomorrow",
  "honestly",
  "frankly",
  "obviously",
  "clearly",
  "sadly",
  "unfortunately",
  "fortunately",
  "luckily",
  "importantly",
  "interestingly",
  "surprisingly",
  "apparently",
  "ideally",
  "generally",
  "typically",
  "usually",
  "often",
  "sometimes",
  "eventually",
  "suddenly",
  "now",
  "here",
  "needless",
  "personally",
  "overall",
  "briefly",
  "ultimately",
  "initially",
  "originally",
  "undoubtedly",
]);

/** "The more … , the more …" and its relatives. */
const COMPARATIVES: ReadonlySet<string> = new Set([
  "more",
  "less",
  "better",
  "worse",
  "greater",
  "higher",
  "lower",
  "bigger",
  "faster",
  "longer",
  "harder",
  "fewer",
  "sooner",
  "earlier",
  "larger",
  "smaller",
  "stronger",
]);

const RELATIVES: ReadonlySet<string> = new Set([
  "who",
  "which",
  "that",
  "whom",
  "whose",
  "where",
]);

const NOUN_MODALS: ReadonlySet<string> = new Set([
  "will",
  "can",
  "may",
  "might",
  "must",
  "shall",
  "do",
  "have",
]);

const PLURAL_NOUNS_WITHOUT_S: ReadonlySet<string> = new Set([
  "data",
  "people",
  "children",
  "women",
  "men",
  "media",
  "criteria",
  "phenomena",
  "i",
  "you",
  "we",
  "they",
]);

/** Plural nouns that are also third-person verbs: read as nouns unless a pronoun precedes. */
const NOUN_VERB_S: ReadonlySet<string> = new Set(
  (
    "samples scores records reports results studies states views tests addresses orders supports " +
    "covers labels issues marks matches names plans posts rates ranks targets trades trains transfers " +
    "triggers heads stresses processes increases decreases changes limits links works needs answers " +
    "claims calls cares costs dates days doubts drops figures forms gains hopes lacks lists looks " +
    "measures notes offers pairs parts points rules sets shares signs sorts sounds steps stops " +
    "surveys terms trends types uses values visits votes years"
  ).split(" ")
);

const SERIES_WORDS: ReadonlySet<string> = new Set(["and", "or", "but", "nor"]);

/** "The moment you quote…", "Every time it rains…": a noun phrase that acts as a subordinator. */
const TIME_OPENERS: ReadonlySet<string> = new Set([
  "the",
  "every",
  "each",
  "any",
  "next",
  "by",
]);
const TIME_NOUNS: ReadonlySet<string> = new Set([
  "moment",
  "instant",
  "minute",
  "time",
  "way",
  "day",
]);

/** Subordinators that, right after "that", close the dependent clause at the next comma. */
const EMBEDDED_SUBORDINATORS: ReadonlySet<string> = new Set([
  "if",
  "when",
  "because",
  "although",
  "though",
  "while",
  "whereas",
  "unless",
  "since",
  "once",
  "until",
  "after",
  "before",
]);

const NOT_VERBS: ReadonlySet<string> = new Set([
  "and",
  "or",
  "but",
  "nor",
  "who",
  "whom",
  "whose",
  "which",
  "that",
  "than",
  "as",
  "so",
  "yet",
  "too",
  "also",
  "either",
  "neither",
  "himself",
  "herself",
  "itself",
  "themselves",
  "ourselves",
]);

const STRONG_ADVERBS: ReadonlySet<string> = new Set([
  "however",
  "therefore",
  "thus",
  "moreover",
  "furthermore",
  "consequently",
  "nevertheless",
  "nonetheless",
  "hence",
  "accordingly",
  "meanwhile",
  "otherwise",
]);

const RE_LY = /^\p{L}{3,}ly$/u;
const RE_ING = /^\p{L}{3,}ing$/u;
const RE_HARD_BREAK = /[;:—–]|\.(?=\s+\p{Lu})/gu;
const RE_ANY_HARD_BREAK = /[;:—–]|\.(?=\s+\p{Lu})/u;
const RE_LEADING_COMMA = /^,/;
const RE_COMMA = /,(?=\s+\S)/g;
const RE_QUOTE_OPEN = /^\s*["“‘]/;
const RE_NEXT_COMMA_OR_END = /^\s*(?:[,.!?;:]|$)/;

/* ------------------------------------------------------------------ verb detection */

type VerbKind = "aux" | "verb";

function pluralLike(prev: string): boolean {
  return (
    PLURAL_NOUNS_WITHOUT_S.has(prev) ||
    (prev.length > 3 &&
      prev.endsWith("s") &&
      !prev.endsWith("ss") &&
      !prev.endsWith("us"))
  );
}

/** Whether tokens[index] reads as a finite verb in this position, and what kind. */
function verbAt(
  tokens: readonly Word[],
  index: number,
  loose = false
): VerbKind | null {
  const word = tokens[index].lower;
  const prev = index > 0 ? tokens[index - 1].lower : "";
  if (isAuxiliary(word)) {
    const nounLike =
      NOUN_MODALS.has(word) &&
      (DETERMINERS.has(prev) || PREPOSITIONS.has(prev));
    return nounLike ? null : "aux";
  }
  if (
    NOUN_SIGNALS.has(prev) ||
    (NOUN_VERB_S.has(word) && !SUBJECT_PRONOUNS.has(prev))
  ) {
    return null;
  }
  // "a note introduced by Source:" — a passive participle, not a finite verb.
  if (tokens[index + 1]?.lower === "by" && word.endsWith("ed")) {
    return null;
  }
  if (!VERB_FORMS.has(word)) {
    // An unlisted past tense: "she decorated", "the market cooled".
    const unlisted =
      word.length > 4 &&
      word.endsWith("ed") &&
      (loose || SUBJECT_PRONOUNS.has(prev));
    return unlisted ? "verb" : null;
  }
  // A bare base form is a verb only after a plural subject ("they study", "the results show").
  if (VERB_BASES.has(word) && !pluralLike(prev)) {
    return null;
  }
  return "verb";
}

/** The first finite verb at or after `from`, stopping at a relative pronoun. */
function firstVerb(
  tokens: readonly Word[],
  from: number,
  limit: number
): { index: number; kind: VerbKind } | null {
  for (let i = from; i < Math.min(tokens.length, limit); i++) {
    const word = tokens[i].lower;
    // A relative, a subordinator, a coordinator or a pronoun means the noun phrase is
    // modified or listed ("an interview you did", "a date when it differs").
    const breaks =
      RELATIVES.has(word) ||
      SUBORDINATORS.has(word) ||
      SERIES_WORDS.has(word) ||
      SUBJECT_PRONOUNS.has(word);
    if (breaks && i > from - 1 && i > 0) {
      return null;
    }
    const kind = verbAt(tokens, i);
    if (kind) {
      return { index: i, kind };
    }
  }
  return null;
}

/* ------------------------------------------------------------------ left clause */

type LeftVerdict = "clause" | "intro" | "no-verb";

function dropLeading(tokens: readonly Word[]): Word[] {
  let start = 0;
  while (start < tokens.length) {
    const word = tokens[start].lower;
    const droppable =
      (COORDINATORS.has(word) && word !== "for") ||
      CONJ_ADVERBS.has(word) ||
      (RE_LY.test(word) && !VERB_FORMS.has(word)) ||
      INTRO_WORDS.has(word);
    if (!droppable) {
      break;
    }
    start++;
  }
  return tokens.slice(start);
}

function isIntroOpening(tokens: readonly Word[]): boolean {
  const first = tokens[0].lower;
  if (
    SUBORDINATORS.has(first) ||
    PREPOSITIONS.has(first) ||
    first === "to" ||
    first === "for"
  ) {
    return true;
  }
  if (TIME_NOUNS.has(tokens[1]?.lower ?? "") && TIME_OPENERS.has(first)) {
    return true;
  }
  // "Running late, …" opens a phrase; "Funding was cut" has a gerund subject and an auxiliary.
  if (RE_ING.test(first) && !isAuxiliary(tokens[1]?.lower ?? "")) {
    return true;
  }
  return first === "the" && COMPARATIVES.has(tokens[1]?.lower ?? "");
}

/** "That is, …" — the spelled-out i.e. */
function isThatIs(tokens: readonly Word[]): boolean {
  return (
    tokens.length === 2 &&
    tokens[0].lower === "that" &&
    tokens[1].lower === "is"
  );
}

/** "…says that if the original is unavailable," — the comma ends an embedded dependent clause. */
function hasEmbeddedSubordinator(tokens: readonly Word[]): boolean {
  return tokens.some(
    (word, i) =>
      i > 0 &&
      tokens[i - 1].lower === "that" &&
      EMBEDDED_SUBORDINATORS.has(word.lower)
  );
}

function judgeLeft(left: readonly Word[]): LeftVerdict {
  const tokens = dropLeading(left);
  if (tokens.length < 2) {
    return left.length > 0 && tokens.length === 0 ? "intro" : "no-verb";
  }
  if (isIntroOpening(tokens) || isThatIs(tokens)) {
    return "intro";
  }
  if (hasEmbeddedSubordinator(tokens)) {
    return "no-verb";
  }
  for (let i = 1; i < tokens.length; i++) {
    if (verbAt(tokens, i)) {
      return "clause";
    }
  }
  return "no-verb";
}

/* ------------------------------------------------------------------ right clause */

interface Head {
  kind: Exclude<SpliceKind, "adverb">;
  confidence: SpliceConfidence;
  /** Index into the head tokens of the clause's verb. */
  verbIndex: number;
}

function pronounHead(
  head: readonly Word[],
  afterConjunction = false
): Head | null {
  let j = 1;
  while (
    j < head.length &&
    j <= 3 &&
    (MID_ADVERBS.has(head[j].lower) || RE_LY.test(head[j].lower))
  ) {
    j++;
  }
  const next = head[j]?.lower;
  if (!next) {
    return null;
  }
  const known =
    isAuxiliary(next) || VERB_FORMS.has(next) || next.endsWith("n't");
  if (known) {
    return { kind: "pronoun", confidence: "likely", verbIndex: j };
  }
  // An unlisted verb ("I think", "they omit"). "you fool" is a vocative, so "you" needs a listed verb.
  const unlisted =
    (afterConjunction || head[0].lower !== "you") &&
    !NOT_VERBS.has(next) &&
    !DETERMINERS.has(next) &&
    !PREPOSITIONS.has(next);
  return unlisted
    ? { kind: "pronoun", confidence: "possible", verbIndex: j }
    : null;
}

const THERE_VERBS: ReadonlySet<string> = new Set([
  "is",
  "are",
  "was",
  "were",
  "has",
  "have",
  "exists",
  "remains",
  "seems",
  "appears",
  "will",
  "would",
  "could",
  "may",
  "might",
  "must",
  "isn't",
  "aren't",
  "wasn't",
  "weren't",
]);

function demonstrativeHead(head: readonly Word[]): Head | null {
  const next = head[1]?.lower;
  if (!next) {
    return null;
  }
  if (isAuxiliary(next)) {
    return { kind: "demonstrative", confidence: "likely", verbIndex: 1 };
  }
  const inflected = VERB_FORMS.has(next) && !VERB_BASES.has(next);
  return inflected
    ? { kind: "demonstrative", confidence: "possible", verbIndex: 1 }
    : null;
}

function nounPhraseHead(
  head: readonly Word[],
  capitalName: boolean
): Head | null {
  if (capitalName && INTRO_WORDS.has(head[0].lower)) {
    return null;
  }
  const found = firstVerb(head, 1, capitalName ? 3 : 8);
  if (!found) {
    return null;
  }
  const kind = capitalName ? "name" : "determiner";
  const strong = found.kind === "aux" && found.index <= 6;
  return {
    kind,
    confidence: strong ? "likely" : "possible",
    verbIndex: found.index,
  };
}

function clauseHead(
  head: readonly Word[],
  afterConjunction = false
): Head | null {
  if (head.length < 2) {
    return null;
  }
  const first = head[0].lower;
  if (SUBJECT_PRONOUNS.has(first)) {
    return pronounHead(head, afterConjunction);
  }
  if (first === "there") {
    return THERE_VERBS.has(head[1].lower)
      ? { kind: "there", confidence: "likely", verbIndex: 1 }
      : null;
  }
  if (first === "this" || first === "these" || first === "those") {
    return demonstrativeHead(head) ?? nounPhraseHead(head, false);
  }
  if (DETERMINERS.has(first) && first !== "that") {
    return nounPhraseHead(head, false);
  }
  if (
    startsUpper(head[0].text) &&
    !COORDINATORS.has(first) &&
    !SUBORDINATORS.has(first)
  ) {
    return nounPhraseHead(head, true);
  }
  return null;
}

/** After "however,": a bare noun and a verb, as in "however, costs rose sharply". */
function bareSubjectHead(
  head: readonly Word[],
  adverb: string | null
): Head | null {
  if (
    !(adverb && STRONG_ADVERBS.has(adverb)) ||
    head.length < 2 ||
    COORDINATORS.has(head[0].lower) ||
    SUBORDINATORS.has(head[0].lower)
  ) {
    return null;
  }
  const verb = verbAt(head, 1);
  return verb
    ? { kind: "determiner", confidence: "possible", verbIndex: 1 }
    : null;
}

interface RightVerdict extends Head {
  adverb: string | null;
  /** Number of leading tokens taken by the adverb or transition. */
  adverbWords: number;
}

function transitionLength(right: readonly Word[]): number {
  for (const phrase of TRANSITION_PHRASES) {
    if (phrase.every((word, i) => right[i]?.lower === word)) {
      return phrase.length;
    }
  }
  return CONJ_ADVERBS.has(right[0]?.lower ?? "") ? 1 : 0;
}

/** The tokens up to and including the first one followed by a comma. */
function untilComma(text: string, tokens: readonly Word[]): Word[] {
  const index = tokens.findIndex((w) => text[w.end] === ",");
  return index === -1 ? [...tokens] : tokens.slice(0, index + 1);
}

/** "…, the same way X does" and "…, the way X": an adverbial phrase, not a clause. */
function isAdverbialNoun(head: readonly Word[]): boolean {
  const [a, b] = [head[0]?.lower, head[1]?.lower];
  const idiom = b === "same" || b === "way" || b === "moment" || b === "time";
  const pair =
    b === "other" || b === "rest" || b === "latter" || b === "former";
  return a === "the" && (idiom || pair);
}

function judgeRight(
  text: string,
  right: readonly Word[],
  afterConjunction = false
): RightVerdict | null {
  const skipped = transitionLength(right);
  const rest = untilComma(text, right.slice(skipped));
  if (isAdverbialNoun(rest)) {
    return null;
  }
  const adverbText =
    skipped > 0
      ? right
          .slice(0, skipped)
          .map((w) => w.lower)
          .join(" ")
      : null;
  const head =
    clauseHead(rest, afterConjunction) ?? bareSubjectHead(rest, adverbText);
  if (!head) {
    return null;
  }
  const adverb =
    skipped > 0
      ? right
          .slice(0, skipped)
          .map((w) => w.lower)
          .join(" ")
      : null;
  return { ...head, adverb, adverbWords: skipped };
}

/** "…, he said, …" and "…, I think." — a tag on the clause, not a second clause. */
function isAttribution(
  text: string,
  right: readonly Word[],
  verdict: RightVerdict
): boolean {
  if (verdict.kind !== "pronoun") {
    return false;
  }
  const verb = right[verdict.adverbWords + verdict.verbIndex];
  if (!(verb && ATTRIBUTION_VERBS.has(verb.lower.replace("n't", "")))) {
    return false;
  }
  return RE_NEXT_COMMA_OR_END.test(text.slice(verb.end, verb.end + 3));
}

/* ------------------------------------------------------------------ fixes */

function buildFixes(
  sentence: string,
  commaAt: number,
  right: readonly Word[],
  sentenceStart: number,
  verdict: RightVerdict
): SpliceFix[] {
  const before = sentence.slice(0, commaAt);
  let after = sentence.slice(commaAt + 1);
  // "however the …" gains the comma it should have had: "; however, the …".
  const adverbWord = verdict.adverb
    ? right[verdict.adverbWords - 1]
    : undefined;
  if (
    adverbWord &&
    verdict.adverb &&
    !verdict.adverb.includes(" ") &&
    verdict.adverb !== "then"
  ) {
    const adverbEnd = adverbWord.end - sentenceStart - commaAt - 1;
    if (!after.slice(adverbEnd).startsWith(",")) {
      after = `${after.slice(0, adverbEnd)},${after.slice(adverbEnd)}`;
    }
  }
  const fixes: SpliceFix[] = [
    {
      kind: "period",
      sentence: `${before}. ${capitalizeFirst(after.trimStart())}`,
    },
    { kind: "semicolon", sentence: `${before};${after}` },
  ];
  const conjunction = conjunctionFix(
    sentence,
    commaAt,
    right,
    sentenceStart,
    verdict
  );
  if (conjunction) {
    fixes.push(conjunction);
  }
  return fixes;
}

function conjunctionFix(
  sentence: string,
  commaAt: number,
  right: readonly Word[],
  sentenceStart: number,
  verdict: RightVerdict
): SpliceFix | null {
  const before = sentence.slice(0, commaAt);
  if (!verdict.adverb) {
    return {
      kind: "conjunction",
      sentence: `${before}, and${sentence.slice(commaAt + 1)}`,
    };
  }
  const conjunction = ADVERB_TO_CONJUNCTION.get(verdict.adverb);
  const adverbWord = right[verdict.adverbWords - 1];
  if (!(conjunction && adverbWord)) {
    return null;
  }
  const tail = sentence
    .slice(adverbWord.end - sentenceStart)
    .replace(RE_LEADING_COMMA, "");
  return { kind: "conjunction", sentence: `${before}, ${conjunction}${tail}` };
}

/* ------------------------------------------------------------------ scan */

function clauseText(
  text: string,
  tokens: readonly Word[],
  end: number
): string {
  if (tokens.length === 0) {
    return "";
  }
  return excerpt(text.slice(tokens[0].start, end), 160);
}

function noteFor(
  text: string,
  left: readonly Word[],
  right: readonly Word[],
  verdict: RightVerdict
): SpliceNote | null {
  const leftCore = dropLeading(left);
  const rightCore = untilComma(text, right.slice(verdict.adverbWords));
  if (
    !verdict.adverb &&
    leftCore.length > 0 &&
    leftCore.length <= 2 &&
    rightCore.length <= 2
  ) {
    return "short";
  }
  const sameOpening =
    leftCore.length >= 2 &&
    rightCore.length >= 2 &&
    leftCore[0].lower === rightCore[0].lower &&
    leftCore[1].lower === rightCore[1].lower;
  return sameOpening ? "parallel" : null;
}

interface SentenceScan {
  findings: SpliceFinding[];
  commas: number;
}

/** Hard breaks (semicolons, colons, dashes) inside the left span reset where the clause starts. */
function lastHardBreak(text: string, from: number, to: number): number {
  let at = -1;
  for (const match of text.slice(from, to).matchAll(RE_HARD_BREAK)) {
    at = from + match.index + 1;
  }
  return at;
}

function lastBreakAfter(text: string, from: number, to: number): number {
  const rest = text.slice(from + 1, to);
  const at = rest.search(RE_ANY_HARD_BREAK);
  return at === -1 ? to : from + 1 + at;
}

function rightTokens(
  text: string,
  words: readonly Word[],
  comma: number,
  sentenceEnd: number
): Word[] {
  const nextBreak = lastBreakAfter(text, comma, sentenceEnd);
  return words
    .filter((w) => w.start > comma && w.end <= nextBreak)
    .slice(0, 12);
}

const SERIES_CLOSERS: ReadonlySet<string> = new Set([
  "and",
  "or",
  "but",
  "nor",
]);

/**
 * ", and she left": a coordinator that closes a series. It must be followed by something
 * that can start a clause: a pronoun, a determiner, a name, or a noun and a verb.
 */
function closesClauseSeries(text: string, right: readonly Word[]): boolean {
  if (!SERIES_CLOSERS.has(right[0].lower)) {
    return false;
  }
  const after = right.slice(1);
  const first = after[0];
  if (!first) {
    return false;
  }
  const word = first.lower;
  if (SUBJECT_PRONOUNS.has(word) || DETERMINERS.has(word) || word === "there") {
    return true;
  }
  return (
    judgeRight(text, after, true) !== null ||
    (after.length > 1 && verbAt(after, 1) !== null)
  );
}

interface Candidate {
  finding: SpliceFinding;
  /** Offset of the semicolon/colon/dash that ends this comma's stretch, or the sentence end. */
  limit: number;
}

function classifyComma(
  text: string,
  left: readonly Word[],
  right: readonly Word[]
): { verdict: RightVerdict | null; leftVerdict: LeftVerdict; splice: boolean } {
  const verdict = judgeRight(text, right);
  const leftVerdict = judgeLeft(left);
  const splice =
    verdict !== null &&
    leftVerdict === "clause" &&
    !isAttribution(text, right, verdict);
  return { verdict, leftVerdict, splice };
}

/** Commas inside quotations or brackets, or just before an opening quotation mark. */
function isShielded(
  text: string,
  sentence: Sentence,
  quotes: readonly Span[],
  comma: number
): boolean {
  return (
    insideAny(quotes, comma, comma + 1) ||
    bracketDepth(text, sentence.start, comma) > 0 ||
    RE_QUOTE_OPEN.test(text.slice(comma + 1, comma + 4))
  );
}

function scanSentence(
  text: string,
  sentence: Sentence,
  quotes: readonly Span[]
): SentenceScan {
  const words = wordsIn(text, sentence.start, sentence.end);
  const candidates: Candidate[] = [];
  const closers: number[] = [];
  let clauseStart = sentence.start;
  let commas = 0;
  // True while an introductory phrase has not yet been followed by its main clause
  // ("For slides, quizzes, and videos the instructor made, the LMS is …").
  let introPending = false;
  for (const match of sentence.text.matchAll(RE_COMMA)) {
    const comma = sentence.start + match.index;
    if (isShielded(text, sentence, quotes, comma)) {
      continue;
    }
    clauseStart = Math.max(
      clauseStart,
      lastHardBreak(text, clauseStart, comma)
    );
    const left = words.filter((w) => w.start >= clauseStart && w.end <= comma);
    const right = rightTokens(text, words, comma, sentence.end);
    if (left.length === 0 || right.length === 0) {
      continue;
    }
    commas++;
    if (COORDINATORS.has(right[0].lower)) {
      if (closesClauseSeries(text, right)) {
        closers.push(comma);
      }
      clauseStart = right[0].end;
      continue;
    }
    const { verdict, leftVerdict, splice } = classifyComma(text, left, right);
    if (splice && verdict) {
      if (!introPending) {
        candidates.push({
          finding: makeFinding(text, sentence, comma, left, right, verdict),
          limit: lastBreakAfter(text, comma, sentence.end),
        });
      }
      introPending = false;
      clauseStart = comma + 1;
    } else if (leftVerdict === "intro") {
      introPending = verdict === null;
      clauseStart = comma + 1;
    }
  }
  return { findings: withoutSeries(candidates, closers), commas };
}

/**
 * "She came, she saw, and she conquered" is a series of clauses, which handbooks allow;
 * a candidate followed by such a closing ", and …" is dropped. Two or more candidates
 * with no closer are still reported, but only as "possible".
 */
function withoutSeries(
  candidates: readonly Candidate[],
  closers: readonly number[]
): SpliceFinding[] {
  const kept = candidates.filter(
    (c) =>
      !closers.some((closer) => closer > c.finding.comma && closer < c.limit)
  );
  if (kept.length < 2) {
    return kept.map((c) => c.finding);
  }
  return kept.map((c) => ({
    ...c.finding,
    confidence: "possible" as const,
    note: c.finding.note ?? "series",
  }));
}

function makeFinding(
  text: string,
  sentence: Sentence,
  comma: number,
  left: readonly Word[],
  right: readonly Word[],
  verdict: RightVerdict
): SpliceFinding {
  const note = noteFor(text, left, right, verdict);
  const lastLeft = left.at(-1) as Word;
  const firstRight = right[0];
  const rightEnd = right.at(-1)?.end ?? firstRight.end;
  return {
    start: lastLeft.start,
    end: firstRight.end,
    comma,
    kind: verdict.adverb ? "adverb" : verdict.kind,
    confidence: note ? "possible" : verdict.confidence,
    note,
    adverb: verdict.adverb,
    sentenceStart: sentence.start,
    sentence: sentence.text,
    leftClause: clauseText(text, dropLeading(left), comma),
    rightClause: clauseText(text, right.slice(verdict.adverbWords), rightEnd),
    fixes: buildFixes(
      sentence.text,
      comma - sentence.start,
      right,
      sentence.start,
      verdict
    ),
  };
}

export function findCommaSplices(text: string): CommaSpliceReport {
  const sentences = splitSentences(text);
  const headings = headingFlags(sentences);
  const quotes = quotedSpans(text);
  const findings: SpliceFinding[] = [];
  let commas = 0;
  let counted = 0;
  for (const [index, sentence] of sentences.entries()) {
    if (headings[index]) {
      continue;
    }
    counted++;
    const scan = scanSentence(text, sentence, quotes);
    findings.push(...scan.findings);
    commas += scan.commas;
  }
  const likely = findings.filter((f) => f.confidence === "likely").length;
  return {
    words: countWords(text),
    sentences: counted,
    commas,
    findings,
    likely,
    possible: findings.length - likely,
    sentencesWithSplice: new Set(findings.map((f) => f.sentenceStart)).size,
  };
}
